/** A single national date and the greeting shown on it. */
export interface NationalDate {
  /** Month, 1-12. */
  month: number;
  /** Day of month, 1-31. */
  day: number;
  /** Greeting text, literal, written in the country's own language. */
  message: string;
}

/** A supported country, its IANA timezones and its national dates. */
export interface NationalGreeting {
  /** Stable identifier, e.g. 'ecuador'. Also selects the flag chip drawn next
   *  to the greeting — see FLAG_DESIGNS in shared/atoms/flag-chip. */
  country: string;
  /** IANA timezones that map to this country. */
  timezones: string[];
  /** National dates for this country. */
  dates: NationalDate[];
}

/**
 * National-date greetings for the countries whose language the app ships
 * (Spanish, German, English). Messages are literal, written in each
 * country's own language. Extend this list to add countries or dates.
 */
export const NATIONAL_GREETINGS: readonly NationalGreeting[] = [
  {
    country: 'ecuador',
    timezones: ['America/Guayaquil', 'Pacific/Galapagos'],
    dates: [
      { month: 8, day: 10, message: '¡Feliz Día de la Independencia, Ecuador!' },
      { month: 5, day: 24, message: '¡Feliz Día de la Batalla de Pichincha!' },
      { month: 10, day: 9, message: '¡Feliz Independencia de Guayaquil!' },
    ],
  },
  {
    country: 'colombia',
    timezones: ['America/Bogota'],
    dates: [
      { month: 7, day: 20, message: '¡Feliz Día de la Independencia, Colombia!' },
      { month: 8, day: 7, message: '¡Feliz Día de la Batalla de Boyacá!' },
    ],
  },
  {
    country: 'peru',
    timezones: ['America/Lima'],
    dates: [
      { month: 7, day: 28, message: '¡Felices Fiestas Patrias, Perú!' },
      { month: 7, day: 29, message: '¡Felices Fiestas Patrias!' },
    ],
  },
  {
    country: 'uruguay',
    timezones: ['America/Montevideo'],
    dates: [
      { month: 8, day: 25, message: '¡Feliz Día de la Independencia, Uruguay!' },
      { month: 7, day: 18, message: '¡Feliz Día de la Jura de la Constitución!' },
    ],
  },
  {
    country: 'argentina',
    timezones: [
      'America/Argentina/Buenos_Aires',
      'America/Argentina/Cordoba',
      'America/Argentina/Mendoza',
      'America/Argentina/Salta',
      'America/Argentina/Tucuman',
      'America/Argentina/San_Juan',
      'America/Argentina/Jujuy',
      'America/Argentina/Catamarca',
      'America/Argentina/La_Rioja',
      'America/Argentina/Rio_Gallegos',
      'America/Argentina/Ushuaia',
      'America/Argentina/San_Luis',
    ],
    dates: [
      { month: 7, day: 9, message: '¡Feliz Día de la Independencia, Argentina!' },
      { month: 5, day: 25, message: '¡Feliz Día de la Revolución de Mayo!' },
      { month: 6, day: 20, message: '¡Feliz Día de la Bandera!' },
    ],
  },
  {
    country: 'spain',
    timezones: ['Europe/Madrid', 'Atlantic/Canary', 'Africa/Ceuta'],
    dates: [
      { month: 10, day: 12, message: '¡Feliz Fiesta Nacional de España!' },
      { month: 12, day: 6, message: '¡Feliz Día de la Constitución!' },
    ],
  },
  {
    country: 'germany',
    timezones: ['Europe/Berlin', 'Europe/Busingen'],
    dates: [
      { month: 10, day: 3, message: 'Alles Gute zum Tag der Deutschen Einheit!' },
      { month: 5, day: 1, message: 'Einen schönen Tag der Arbeit!' },
    ],
  },
  {
    country: 'switzerland',
    timezones: ['Europe/Zurich'],
    dates: [{ month: 8, day: 1, message: 'Schönen Bundesfeiertag!' }],
  },
  {
    country: 'united_states',
    timezones: [
      'America/New_York',
      'America/Detroit',
      'America/Chicago',
      'America/Denver',
      'America/Phoenix',
      'America/Los_Angeles',
      'America/Anchorage',
      'America/Adak',
      'Pacific/Honolulu',
    ],
    dates: [
      { month: 7, day: 4, message: 'Happy Independence Day!' },
      { month: 11, day: 11, message: 'Happy Veterans Day!' },
    ],
  },
];
